/**
 * PATH: apps/hr/src/pages/documents/documents-list/DocumentRow.tsx
 * PURPOSE: Table row component for a single document, plus delete confirmation modal
 * WHY: Separates per-row rendering and delete modal from the main page
 */

import { FileText, Eye, CheckCircle2, Trash2, AlertTriangle, Loader2, X } from 'lucide-react'
import { cn, formatDate } from '../../../lib/utils'
import type { Document as DocType, DocumentType, DocumentStatus } from '../../../types'
import { ConfidenceBadge, StatusBadge } from './StatusBadges'

const TYPE_LABELS: Record<DocumentType, string> = {
  license: 'License',
  certification: 'Certification',
  contract: 'Contract',
  identification: 'ID',
  visa: 'Visa',
  medical: 'Medical',
  training: 'Training',
  other: 'Other',
}

export function DocumentRow({ doc, onClick, onDelete }: { 
  doc: DocType
  onClick: () => void
  onDelete: (e: React.MouseEvent) => void 
}) {
  const isExpiringSoon = doc.daysUntilExpiry !== undefined && doc.daysUntilExpiry > 0 && doc.daysUntilExpiry <= 30
  const isExpired = doc.daysUntilExpiry !== undefined && doc.daysUntilExpiry <= 0

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0" onClick={onClick}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <FileText className="w-5 h-5 text-slate-500" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{doc.documentName}</p>
            <p className="text-xs text-slate-500 truncate">{doc.documentNumber}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
          {TYPE_LABELS[doc.documentType]}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400">{doc.employeeName}</td>
      <td className="py-3 px-4">
        {doc.confidenceScore !== undefined && <ConfidenceBadge confidence={doc.confidenceScore} />}
      </td>
      <td className="py-3 px-4">
        {doc.expiryDate ? (
          <div className={cn('text-sm', isExpired && 'text-red-600 font-medium', isExpiringSoon && !isExpired && 'text-amber-600 font-medium', !isExpired && !isExpiringSoon && 'text-slate-600 dark:text-slate-400')}>
            {formatDate(doc.expiryDate, { day: 'numeric', month: 'short', year: 'numeric' })}
          </div>
        ) : <span className="text-sm text-slate-400">-</span>}
      </td>
      <td className="py-3 px-4">
        {doc.daysUntilExpiry !== undefined ? (
          <span className={cn('text-sm font-semibold', doc.daysUntilExpiry <= 0 ? 'text-red-600' : doc.daysUntilExpiry <= 30 ? 'text-amber-600' : 'text-slate-600 dark:text-slate-400')}>
            {doc.daysUntilExpiry < 0 ? `${Math.abs(doc.daysUntilExpiry)} ago` : doc.daysUntilExpiry === 0 ? 'Today' : doc.daysUntilExpiry}
          </span>
        ) : <span className="text-sm text-slate-400">-</span>}
      </td>
      <td className="py-3 px-4"><StatusBadge status={doc.status} /></td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-1 justify-end">
          <button onClick={(e) => { e.stopPropagation(); onClick() }} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500" title="View Document"><Eye className="w-4 h-4" /></button>
          {doc.status === 'needs_review' && (
            <button onClick={(e) => e.stopPropagation()} className="p-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 rounded-lg text-emerald-600 dark:text-emerald-400" title="Approve Document"><CheckCircle2 className="w-4 h-4" /></button>
          )}
          <button onClick={onDelete} className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg text-red-500" title="Delete Document"><Trash2 className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  )
}

export function DeleteModal({ isOpen, onClose, onConfirm, isDeleting }: {
  isOpen: boolean; onClose: () => void; onConfirm: () => void; isDeleting: boolean
}) {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-sm m-4 p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Delete Document</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Are you sure you want to delete this document? This action cannot be undone.</p>
          <div className="flex items-center justify-center gap-3">
            <button onClick={onClose} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancel</button>
            <button onClick={onConfirm} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2">
              {isDeleting ? (<><Loader2 className="w-4 h-4 animate-spin" />Deleting...</>) : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
